// A fixed, nested lattice adds dots without moving the ones already on the nail.
// 100% matches the original 18 x 18 pattern exactly. Density only selects more
// cells from the same 36 x 36 tile; erased IDs survive later density changes.
export const DOT_DENSITY = Object.freeze({min:25,max:400,step:25,default:100});
export const DOT_RADIUS = 2.5;
export const DOT_TILE_SIZE = 36;
export const DOT_PATTERN_BOUNDS = Object.freeze({x:-100,y:-150,width:300,height:450});
const SPACING=9;
const CELLS=Object.freeze([
  [1,1],[3,3],[3,1],[1,3],
  [0,0],[2,2],[0,2],[2,0],
  [0,1],[2,3],[0,3],[2,1],
  [1,0],[3,2],[3,0],[1,2],
]);
const finite=(value,fallback)=>typeof value==='number'&&Number.isFinite(value)?value:fallback;
const mod=(value,divisor)=>(value%divisor+divisor)%divisor;

export function normalizeDotDensity(value) {
  return Math.round(Math.max(DOT_DENSITY.min,Math.min(DOT_DENSITY.max,finite(value,DOT_DENSITY.default)))/DOT_DENSITY.step)*DOT_DENSITY.step;
}

export function dotFromId(id) {
  if(typeof id!=='string'||!/^[-]?\d{1,2}:[-]?\d{1,2}$/.test(id))return null;
  const [column,row]=id.split(':').map(Number),x=column*SPACING,y=row*SPACING,b=DOT_PATTERN_BOUNDS;
  if(id!==`${column}:${row}`||x<b.x||x>b.x+b.width||y<b.y||y>b.y+b.height)return null;
  return {id,column,row,x,y,r:DOT_RADIUS};
}

export function normalizeDotPattern(value={}) {
  const erasedDots=Array.isArray(value.erasedDots)?[...new Set(value.erasedDots.slice(0,2048).filter(id=>dotFromId(id)))]:[];
  return {dotDensity:normalizeDotDensity(value.dotDensity),erasedDots,dotFlipX:value.dotFlipX===true};
}

export function dotPatternTile(density=DOT_DENSITY.default) {
  return CELLS.slice(0,normalizeDotDensity(density)/DOT_DENSITY.step).map(([column,row])=>({x:column*SPACING,y:row*SPACING,r:DOT_RADIUS}));
}

export function patternDotPoints(layer,{includeErased=false}={}) {
  const {dotDensity,erasedDots}=normalizeDotPattern(layer),b=DOT_PATTERN_BOUNDS;
  const cells=new Set(CELLS.slice(0,dotDensity/DOT_DENSITY.step).map(([column,row])=>`${column}:${row}`));
  const erased=new Set(includeErased?[]:erasedDots),points=[];
  for(let row=Math.ceil(b.y/SPACING);row<=Math.floor((b.y+b.height)/SPACING);row++){
    for(let column=Math.ceil(b.x/SPACING);column<=Math.floor((b.x+b.width)/SPACING);column++){
      const id=`${column}:${row}`;
      if(cells.has(`${mod(column,4)}:${mod(row,4)}`)&&!erased.has(id))points.push({id,column,row,x:column*SPACING,y:row*SPACING,r:DOT_RADIUS});
    }
  }
  return points;
}

export function transformedDotPoint(layer,point) {
  const scale=Math.max(.1,Math.min(3,finite(layer.scale,1))),angle=finite(layer.rotation,0)*Math.PI/180;
  const dx=((layer.dotFlipX===true?100-point.x:point.x)-50)*scale,dy=(point.y-75)*scale;
  return {x:finite(layer.x,50)+dx*Math.cos(angle)-dy*Math.sin(angle),y:finite(layer.y,75)+dx*Math.sin(angle)+dy*Math.cos(angle)};
}

// Same array return contract as eraseStrokeAt, for use with layers.flatMap().
// Removal is by dot identity, not by a paint-coloured patch, so the underlying
// material and any lower layers remain visible through the erased circle.
export function erasePatternDotsAt(layer,point,radius) {
  if(!layer||layer.kind!=='pattern'||layer.type!=='dots'||layer.visible===false||layer.opacity===0||!point||!Number.isFinite(point.x)||!Number.isFinite(point.y)||!Number.isFinite(radius)||radius<=0)return layer?[layer]:[];
  const scale=Math.max(.1,Math.min(3,finite(layer.scale,1))),reach=Math.min(radius,100)+DOT_RADIUS*scale;
  const hits=patternDotPoints(layer).filter(dot=>{const p=transformedDotPoint(layer,dot);return Math.hypot(p.x-point.x,p.y-point.y)<=reach;});
  if(!hits.length)return [layer];
  const state=normalizeDotPattern(layer);
  return [{...layer,...state,erasedDots:[...new Set([...state.erasedDots,...hits.map(dot=>dot.id)])]}];
}
